import React, { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Truck } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, TextArea, Select, Button } from "@/components/ui-kit";
import { listProviders, createProvider, updateProvider, deleteProvider } from "@/lib/api";

export const PROVIDER_TYPES = [
  { id: "aviram", name: "Aviram" },
  { id: "carn", name: "Carn" },
  { id: "verdures", name: "Verdures" },
  { id: "additius", name: "Additius" },
  { id: "envasos", name: "Envasos" },
  { id: "altres", name: "Altres" },
];
export const providerTypeName = (t) => PROVIDER_TYPES.find((x) => x.id === t)?.name || t;

export default function ProviderCatalog() {
  const { data: providers, mutate } = useSWR("providers", listProviders);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const startNew = () => setEditing({ id: null, name: "", nif: "", ptype: "altres", address: "", contact: "", notes: "" });
  const startEdit = (p) => setEditing({ id: p.id, name: p.name, nif: p.nif || "", ptype: p.ptype || "altres", address: p.address || "", contact: p.contact || "", notes: p.notes || "" });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Posa un nom");
    setSaving(true);
    try {
      const payload = { name: editing.name.trim(), nif: editing.nif, ptype: editing.ptype, address: editing.address, contact: editing.contact, notes: editing.notes, active: true };
      if (editing.id) await updateProvider(editing.id, payload);
      else await createProvider(payload);
      toast.success("Proveïdor desat");
      setEditing(null); mutate();
    } catch (err) { toast.error(err.response?.data?.detail || "Error desant"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}"?`)) return;
    try { await deleteProvider(p.id); toast.success("Eliminat"); mutate(); }
    catch { toast.error("Error eliminant"); }
  };

  return (
    <>
      <Header title="Proveïdors" />
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Admin · Pla 5 · Traçabilitat</div>
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Proveïdors</h2>
          </div>
          {!editing && <Button onClick={startNew} testid="provider-new"><Plus size={18} /> Nou</Button>}
        </div>

        {editing && (
          <Card className="mt-6"><div className="space-y-4">
            <div><div className="label-eyebrow mb-2">Nom</div>
              <TextInput testid="provider-name" value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="Ex: Moré S.L." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="label-eyebrow mb-2">NIF</div>
                <TextInput value={editing.nif} onChange={(e) => setEditing((s) => ({ ...s, nif: e.target.value }))} placeholder="B-1234" /></div>
              <div><div className="label-eyebrow mb-2">Tipus</div>
                <Select value={editing.ptype} onChange={(e) => setEditing((s) => ({ ...s, ptype: e.target.value }))}>
                  {PROVIDER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select></div>
            </div>
            <div><div className="label-eyebrow mb-2">Adreça</div>
              <TextInput value={editing.address} onChange={(e) => setEditing((s) => ({ ...s, address: e.target.value }))} /></div>
            <div><div className="label-eyebrow mb-2">Contacte</div>
              <TextInput value={editing.contact} onChange={(e) => setEditing((s) => ({ ...s, contact: e.target.value }))} placeholder="Tel · email" /></div>
            <div><div className="label-eyebrow mb-2">Notes</div>
              <TextArea value={editing.notes} onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))} /></div>
            <div className="flex gap-3">
              <Button onClick={save} disabled={saving} testid="provider-save"><Check size={18} /> {saving ? "Desant…" : "Desar"}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}><X size={18} /> Cancel·lar</Button>
            </div>
          </div></Card>
        )}

        <div className="mt-6 space-y-3">
          {providers?.length === 0 && !editing && <Card className="text-center text-sm text-ink-soft">Encara no hi ha proveïdors. Crea'n un amb "Nou".</Card>}
          {providers?.map((p) => (
            <Card key={p.id} testid={`provider-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-ink-soft"><Truck size={16} /></span>
                    <div className="font-display text-lg font-bold text-ink">{p.name}</div>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
                    <span className="rounded-full bg-cream px-2 py-0.5 font-semibold text-ink">{providerTypeName(p.ptype)}</span>
                    {p.nif && <span>NIF {p.nif}</span>}
                    {p.contact && <span>· {p.contact}</span>}
                  </div>
                  {p.address && <div className="mt-1 text-xs text-ink-soft/70">{p.address}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(p)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar"><Pencil size={16} /></button>
                  <button onClick={() => remove(p)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Eliminar"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  );
}
